import React, { useState, useEffect } from 'react';
import { Card, Upload, Button, Radio, message, Space, Image, Select, Row, Col } from 'antd';
import { UploadOutlined, LoadingOutlined, PlusOutlined } from '@ant-design/icons';
import { workflowAPI } from '../services/api';

const { Option } = Select;

const ImageUploadNode = ({ node, onNodeChange }) => {
  const [imageType, setImageType] = useState('fixed'); // 'fixed' 或 'user_upload'
  const [loading, setLoading] = useState(false);
  const [originalImageUrl, setOriginalImageUrl] = useState('');
  const [newImageUrl, setNewImageUrl] = useState('');
  const [selectedUserImage, setSelectedUserImage] = useState('image1');
  
  // 处理OSS图片URL，确保URL正确显示图片
  const processImageUrl = (url) => {
    if (!url) return '';
    
    // 检查是否为OSS URL
    if (typeof url === 'string' && (url.startsWith('http') || url.includes('oss'))) {
      // 确保URL使用HTTPS协议
      try {
        // 将http替换为https
        const secureUrl = url.replace(/^http:/, 'https:');
        return secureUrl;
      } catch (error) {
        console.error('处理图片URL出错:', error);
        return url.replace(/^http:/, 'https:'); // 简单替换http为https
      }
    }
    
    return url;
  };
  
  // 初始化时处理原始图片URL
  useEffect(() => {
    if (node.inputs?.image) {
      const imageUrl = node.inputs.image;
      // 确保使用https协议
      const processedUrl = processImageUrl(imageUrl);
      setOriginalImageUrl(processedUrl);
      
      // 如果图片URL是OSS格式，则设置为固定图片模式
      if (typeof imageUrl === 'string' && (imageUrl.startsWith('http') || imageUrl.includes('oss'))) {
        setImageType('fixed');
        console.log('OSS图片URL已识别:', processedUrl);
      } else {
        // 否则认为是用户上传模式
        setImageType('user_upload');
        setSelectedUserImage(imageUrl || 'image1');
      }
    }
  }, [node.inputs?.image]);
  
  // 处理选择类型变更
  const handleTypeChange = (e) => {
    const newType = e.target.value;
    setImageType(newType);
    
    // 更新节点数据
    if (newType === 'user_upload') {
      onNodeChange(node.nodeId, {
        image: selectedUserImage
      });
    } else {
      // 如果切换到固定图片但尚未上传，保持原始图片
      if (!newImageUrl && originalImageUrl) {
        setNewImageUrl(originalImageUrl);
      }
    }
  };
  
  // 处理用户图片选择变更
  const handleUserImageChange = (value) => {
    setSelectedUserImage(value);
    
    // 更新节点数据
    if (imageType === 'user_upload') {
      onNodeChange(node.nodeId, {
        image: value
      });
    }
  };
  
  // 处理图片上传前检查
  const beforeUpload = (file) => {
    const isImage = file.type.startsWith('image/');
    if (!isImage) {
      message.error('只能上传图片文件!');
      return false;
    }
    
    const isLt10M = file.size / 1024 / 1024 < 10;
    if (!isLt10M) {
      message.error('图片大小不能超过10MB!');
      return false;
    }
    
    return true;
  };
  
  // 自定义上传操作
  const customUpload = async ({ file, onSuccess, onError }) => {
    try {
      setLoading(true);
      const response = await workflowAPI.uploadImage(file);
      
      if (response.success) {
        // 设置新图片URL，确保使用https协议
        const uploadedUrl = response.data.url;
        // 使用processImageUrl函数处理URL以确保使用https
        const processedUrl = processImageUrl(uploadedUrl);
        setNewImageUrl(processedUrl);
        
        // 更新节点数据，保存https URL到数据库
        onNodeChange(node.nodeId, {
          image: processedUrl // 保存处理后的URL到数据库
        });
        
        message.success('图片上传成功');
        onSuccess(response, file);
      } else {
        message.error('上传失败：' + (response.message || '未知错误'));
        onError(new Error('上传失败'));
      }
    } catch (error) {
      console.error('图片上传失败:', error);
      message.error('图片上传失败: ' + (error.message || '未知错误'));
      onError(error);
    } finally {
      setLoading(false);
    }
  };
  
  // 上传按钮
  const uploadButton = (
    <div>
      {loading ? <LoadingOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8 }}>上传图片</div>
    </div>
  );

  // 处理图片加载错误
  const handleImageError = (e) => {
    console.error('图片加载失败:', e);
    message.error('图片加载失败，请检查URL或重新上传');
  };
  
  // 占位图base64，简化版本，避免过长字符串
  const fallbackImage = "data:image/png;base64,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";
  
  return (
    <Card 
      title={`图像节点 (ID: ${node.nodeId})`}
      style={{ marginBottom: 16 }}
    >
      <Radio.Group 
        value={imageType} 
        onChange={handleTypeChange}
        style={{ marginBottom: 16 }}
      >
        <Radio value="fixed">固定图片</Radio>
        <Radio value="user_upload">用户上传</Radio>
      </Radio.Group>
      
      {imageType === 'fixed' && (
        <div>
          <Row gutter={16}>
            {/* 左侧显示原图 */}
            <Col span={12}>
              <div style={{ marginBottom: 8 }}>原始图片</div>
              {originalImageUrl ? (
                <div style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                  <Image
                    src={originalImageUrl}
                    alt="原始图片"
                    style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }}
                    fallback={fallbackImage}
                    onError={handleImageError}
                  />
                  <div style={{ wordBreak: 'break-all', fontSize: '12px', marginTop: '8px', color: '#888' }}>
                    {originalImageUrl}
                  </div>
                </div>
              ) : (
                <div style={{ 
                  width: '100%', 
                  height: 200, 
                  background: '#f0f0f0', 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center' 
                }}>
                  无原始图片
                </div>
              )}
            </Col>
            
            {/* 右侧显示新上传图 */}
            <Col span={12}>
              <div style={{ marginBottom: 8 }}>新上传图片</div>
              <Upload
                name="image"
                listType="picture-card"
                showUploadList={false}
                beforeUpload={beforeUpload}
                customRequest={customUpload}
                style={{ width: '100%' }}
              >
                {newImageUrl ? (
                  <div style={{ border: '1px solid #f0f0f0', padding: '8px', borderRadius: '4px' }}>
                    <Image
                      src={newImageUrl}
                      alt="新上传图片"
                      style={{ width: '100%', maxHeight: '200px', objectFit: 'contain' }}
                      preview={false}
                      fallback={fallbackImage}
                      onError={handleImageError}
                    />
                    <div style={{ wordBreak: 'break-all', fontSize: '12px', marginTop: '8px', color: '#888' }}>
                      {newImageUrl}
                    </div>
                  </div>
                ) : (
                  uploadButton
                )}
              </Upload>
              <div style={{ marginTop: 8 }}>
                {newImageUrl && (
                  <Button 
                    danger 
                    onClick={() => {
                      setNewImageUrl('');
                      // 重置为原始值
                      onNodeChange(node.nodeId, { image: originalImageUrl });
                    }}
                  >
                    撤销更改
                  </Button>
                )}
              </div>
            </Col>
          </Row>
        </div>
      )}
      
      {imageType === 'user_upload' && (
        <div>
          <Row gutter={16}>
            <Col span={12}>
              <div style={{ marginBottom: 8 }}>原始设置</div>
              <div>{originalImageUrl || '无原始设置'}</div>
            </Col>
            <Col span={12}>
              <div style={{ marginBottom: 8 }}>新设置</div>
              <Select
                style={{ width: '100%' }}
                value={selectedUserImage}
                onChange={handleUserImageChange}
              >
                {Array.from({ length: 15 }, (_, i) => i + 1).map(num => (
                  <Option key={num} value={`image${num}`}>图片 {num}</Option>
                ))}
              </Select>
            </Col>
          </Row>
        </div>
      )}
      
      <div style={{ marginTop: 16 }}>
        <p>
          <strong>当前值:</strong> {node.inputs.image}
        </p>
        {imageType === 'user_upload' && (
          <p>
            <strong>设置为:</strong> {selectedUserImage}
          </p>
        )}
        {imageType === 'fixed' && newImageUrl && newImageUrl !== originalImageUrl && (
          <p>
            <strong>设置为:</strong> {newImageUrl}
          </p>
        )}
      </div>
    </Card>
  );
};

export default ImageUploadNode;